import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import type {
	CameraSoundDef,
	CameraSoundName,
	CameraSoundOption,
} from 'src/components/Camera/types/camera-sounds';

const _format = get(format);

export const DEFAULT_SOUND_ICON = 'fas fa-volume-high';

/** Camera sound definitions: icon and translation. */
export const CAMERA_SOUND_DEFS: Record<CameraSoundName, CameraSoundDef> = {
	alert: { i18n: 'camera_play_sound_alert', default: 'Alert', icon: 'fas fa-bell' },
	'ding-dong': {
		i18n: 'camera_play_sound_ding_dong',
		default: 'Ding-dong',
		icon: 'fas fa-bell-concierge',
	},
	notification: {
		i18n: 'camera_play_sound_notification',
		default: 'Notification',
		icon: 'fas fa-message-dots',
	},
};

/** Sound names the firmware plays, ordered as in `CAMERA_SOUND_DEFS`. */
export const CAMERA_SOUND_NAMES = Object.keys(CAMERA_SOUND_DEFS) as CameraSoundName[];

/** Whether the firmware plays a built-in sound under this name. */
export const isCameraSoundName = (sound?: unknown): sound is CameraSoundName =>
	CAMERA_SOUND_NAMES.includes(sound as CameraSoundName);

/*
 * @param sound - A sound name chosen in the UI or read from a scene.
 * @returns The shipped definition, or undefined for a sound the firmware does not play.
 */
export const getCameraSoundDef = (sound?: unknown): CameraSoundDef | undefined =>
	isCameraSoundName(sound) ? CAMERA_SOUND_DEFS[sound] : undefined;

export const getCameraSoundIcon = (sound?: unknown): string =>
	getCameraSoundDef(sound)?.icon ?? DEFAULT_SOUND_ICON;

/*
 * Human-readable name for a built-in sound.
 *
 * A sound we ship no definition for still renders, falling back to its raw name
 * rather than to nothing.
 *
 * @param sound - A sound name chosen in the UI or read from a scene.
 * @returns The translated sound name.
 */
export const getCameraSoundLabel = (sound?: unknown): string => {
	const def = getCameraSoundDef(sound);

	return def ? _format(def.i18n, { default: def.default }) : String(sound ?? '');
};

/*
 * The built-in sounds as selectable cards, so a picker renders them without
 * knowing how a definition is shaped.
 *
 * @returns One option per sound, ordered as in `CAMERA_SOUND_DEFS`.
 */
export const getCameraSoundOptions = (): CameraSoundOption[] =>
	CAMERA_SOUND_NAMES.map((sound) => ({
		id: sound,
		title: getCameraSoundLabel(sound),
		icon: CAMERA_SOUND_DEFS[sound].icon,
	}));
